import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Flex, Box } from 'reflexbox';
import {
  Checkbox,
  Heading,
  Panel,
  PanelHeader,
  Text,
} from 'rebass';

import { sumGearUpgradeCost } from 'shared/items';
import { getGemInfo } from 'shared/gems';
import { getEnchants } from 'shared/enchants';
import { getSpecInfo } from 'shared/character';
import GameData from 'shared/gameData';

import {
  If,
  Responsive,
  WowIcon,
  WowItemIcon,
} from 'frontend/components';
import config from 'frontend/config';
import TalentTree from 'frontend/components/Simbot/TalentTree';
import UpgradeCost from 'frontend/components/Simbot/Optimize/UpgradeCost';

const shouldHighlightItem = (item, slot) => {
  return (item && !item.equipped && !item.originalIsEquipped)
    || (item?.customItem)
    || (item?.equippedSlot && item.equippedSlot !== slot);
};

const TopGearItem = ({
  item,
  highlight,
  highlightEnchant,
  highlightGems,
  setPieceIds,
  classId,
  specId,
  slot,
  align,
}) => {
  const isLeftCol = align === 'left';

  const iconSize = 42;

  const icon = (
    <Box mr={1} ml={1} my={0} py={0} style={{ lineHeight: 1, height: iconSize + 2 }}>
      <WowItemIcon
        item={item}
        setPieceIds={setPieceIds}
        classId={classId}
        specId={specId}
        size={iconSize}
        slot={slot}
      />
    </Box>
  );

  let itemLevelEl;
  let enchantEl;
  let gemEl;
  let effectEl;
  let name;
  if (item) {
    const enchant = _.find(getEnchants(), { id: parseInt(item.enchant_id, 10) });
    const gems = _.map(
      _.filter(_.split(item.gem_id, '/')),
      g => _.find(getGemInfo(), { itemId: parseInt(g, 10) }),
    );
    const gemBonusIds = _.split(item.gem_bonus_id, '/');

    itemLevelEl = (
      <Box
        style={{ fontSize: '12px', fontWeight: 'bold' }}
        mr={isLeftCol ? 1 : 0}
        ml={isLeftCol ? 0 : 1}
      >
        {item.itemLevel}
      </Box>
    );
    enchantEl = enchant && (
      <Flex
        align="center"
        style={{
          height: highlightEnchant ? 22 : 16,
          padding: 1,
          border: highlightEnchant ? `1px solid ${config.colors.orange}` : null,
          lineHeight: 1,
        }}
        mr={isLeftCol ? 1 : 0}
        ml={isLeftCol ? 0 : 1}
      >
        <Box>
          <WowIcon
            icon="inv_misc_enchantedscroll"
            size={14}
            type="item"
            id={enchant.itemId}
            craftingQuality={enchant.craftingQuality}
          />
        </Box>
        <Box
          style={{
            fontSize: '12px',
            color: config.colors.green,
            padding: '0px 3px 0px 3px',
          }}
        >
          {enchant.name}
        </Box>
      </Flex>
    );
    const gemHighlightColor = _.get(item, 'addSocket') ? config.colors.blue : config.colors.orange;
    gemEl = gems.length > 0 && item.quality !== 6 && (
      <Flex
        align="center"
      >
        {_.filter(gems).map((gem, i) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={`${gem.id}-${i}`}
            style={{
              height: highlightGems ? 22 : 16,
              padding: 1,
              border: highlightGems ? `2px solid ${gemHighlightColor}` : null,
              lineHeight: 1,
            }}
          >
            <WowIcon
              key={i} // eslint-disable-line react/no-array-index-key
              icon={gem.itemIcon}
              id={gem.itemId}
              craftingQuality={gem.craftingQuality}
              type="item"
              size={14}
              noLink
              extra={{
                bonus: gemBonusIds[i],
              }}
            />
          </Box>
        ))}
      </Flex>
    );

    const bonusIds = _.split(item.bonus_id, '/');
    const effect = _.first(_.filter(_.map(bonusIds, bid => GameData.BonusEffects[bid])));
    if (effect) {
      effectEl = (
        <Text small color="blue">{effect.spell.name}</Text>
      );
    }

    name = (
      <Text
        bold
        color={config.colors.items[item.quality]}
        size={14}
        style={{ lineHeight: 1.2 }}
      >
        {item.name}
      </Text>
    );
  }
  return (
    <Box
      style={{
        border: highlight ? `1px solid ${config.colors.orange}` : 'transparent',
        backgroundColor: highlight ? '#1f1707' : 'transparent',
        margin: '0 3px 3px 3px',
        padding: highlight ? 4 : 5, // account for additional border pixels offsetting the grid
      }}
    >
      <If
        condition={isLeftCol}
        then={() => (
          <Flex
            align="center"
            justify="flex-start"
            style={{
              textAlign: 'left',
            }}
          >
            {icon}
            <Box>
              {name}
              <Flex
                align="center"
                justify="flex-start"
              >
                {itemLevelEl}
                {enchantEl}
                {gemEl}
              </Flex>
              {effectEl}
            </Box>
          </Flex>
        )}
        else={() => (
          <Flex
            align="center"
            justify="flex-end"
            style={{
              textAlign: 'right',
            }}
          >
            <Box>
              {name}
              <Flex
                align="center"
                justify="flex-end"
              >
                {gemEl}
                {enchantEl}
                {itemLevelEl}
              </Flex>
              {effectEl}
            </Box>
            {icon}
          </Flex>
        )}
      />
    </Box>
  );
};

TopGearItem.propTypes = {
  // eslint-disable-next-line
  item: PropTypes.object,
  highlight: PropTypes.bool,
  highlightEnchant: PropTypes.bool,
  highlightGems: PropTypes.bool,
  // eslint-disable-next-line
  setPieceIds: PropTypes.array.isRequired,
  classId: PropTypes.number.isRequired,
  specId: PropTypes.number.isRequired,
  slot: PropTypes.string.isRequired,
  align: PropTypes.string,
};
TopGearItem.defaultProps = {
  item: null,
  highlight: false,
  highlightEnchant: false,
  highlightGems: false,
  align: 'left',
};

const TopGearPanel = ({
  topActor,
  focusActor,
  classId,
  specName,
  baseTalents,
  level,
  preferEquipped,
  onClickPreferEquipped,
}) => {
  const topCombo = focusActor;

  if (!topCombo) {
    return null;
  }

  const setPieceIds = _.map(topCombo.gear, 'id');
  const specInfo = getSpecInfo(classId, specName);
  const specId = specInfo?.id;

  const slots = [
    [
      'head',
      'neck',
      'shoulder',
      'back',
      'chest',
      // 'shirt',
      // 'tabard',
      'wrist',
    ], [
      'hands',
      'waist',
      'legs',
      'feet',
      'finger1',
      'finger2',
      'trinket1',
      'trinket2',
    ],
  ];

  const upgradeCurrencyUsed = sumGearUpgradeCost(topCombo.gear);

  const closeEnough = topActor.origIdx !== focusActor.origIdx;

  return (
    <Panel theme="primary" pt={2} pb={1} mt={1}>
      <PanelHeader p={1} mb={1}>
        <Flex justify="space-between" auto>
          <Heading level={4}>Your Top Gear</Heading>
          <Checkbox
            name="preferEquipped"
            label="Prefer Equipped if Sidegrade"
            checked={preferEquipped}
            onChange={onClickPreferEquipped}
            backgroundColor={config.colors.black}
            style={{ color: '#222', opacity: 1 }}
            pb={0}
            mb={0}
          />
        </Flex>
      </PanelHeader>
      <If
        condition={topCombo.equipped}
        then={() => (
          <Box mb={1}>
            <Flex justify="center">
              <Heading level={4}>
                Your currently equipped gear is the top gear
              </Heading>
            </Flex>
            <Flex justify="center">
              <Text small>
                <If
                  condition={closeEnough}
                  then={() => (
                    <>
                      * Equipped gear is a
                      {' '}
                      <a
                        href="https://support.raidbots.com/article/61-top-gear-sidegrades"
                        rel="noopener noreferrer"
                        target="_blank"
                        title="More info about Top Gear and sidegrades"
                      >
                        sidegrade
                      </a>
                      {' '}
                      of the top result
                    </>
                  )}
                />
                {/*
                  Empty nbsp to keep vertical height of the page the same. Easier to compare similar
                  reports without the random height difference
                */}
                &nbsp;
              </Text>
            </Flex>
          </Box>
        )}
      />
      <Flex justify="center" mb={2}>
        <Flex justify="space-between" align="center" wrap>
          <TalentTree
            specId={specId}
            level={level}
            talents={topCombo.talents || baseTalents}
            width={700}
            strokeWidth={1}
            name={topCombo.talentsName}
          />
        </Flex>
      </Flex>
      <Flex wrap justify="center">
        {_.map(slots, (column, colIdx) => (
          <Box md={6} sm={12} key={colIdx}>
            {_.map(column, slot => {
              const item = topCombo.gear[slot];
              const highlight = shouldHighlightItem(item, slot);
              const highlightEnchant = item && item.enchantDiff;
              const isLeftCol = colIdx === 0;
              return (
                <TopGearItem
                  key={slot}
                  item={item}
                  highlight={highlight}
                  highlightEnchant={highlightEnchant}
                  highlightGems={item && item.gemDiff}
                  align={isLeftCol ? 'left' : 'right'}
                  setPieceIds={setPieceIds}
                  slot={slot}
                  classId={classId}
                  specId={specId}
                />
              );
            })}
          </Box>
        ))}
      </Flex>
      <Flex wrap>
        {_.map(['main_hand', 'off_hand'], slot => {
          const item = topCombo.gear[slot];
          return (
            <Box md={6} sm={12} key={slot}>
              <Responsive size="large">
                {(isLarge) => {
                  return (
                    <TopGearItem
                      item={item}
                      highlight={shouldHighlightItem(item, slot)}
                      highlightEnchant={item && item.enchantDiff}
                      highlightGems={item && item.gemDiff}
                      align={(isLarge && slot === 'main_hand') ? 'right' : 'left'}
                      setPieceIds={setPieceIds}
                      slot={slot}
                      classId={classId}
                      specId={specId}
                    />
                  );
                }}
              </Responsive>
            </Box>
          );
        })}
      </Flex>
      <If
        condition={!topCombo.equipped}
        then={() => (
          <Flex justify="center" mt={1}>
            <Text small bold>
              Changes from your equipped gear are highlighted
            </Text>
          </Flex>
        )}
        else={() => (
          <Flex justify="center" mt={1}>
            <Text small>
              Your equipped gear is always simmed
            </Text>
          </Flex>
        )}
      />
      <If
        condition={_.size(upgradeCurrencyUsed)}
        then={() => (
          <Flex justify="center" mt={1} style={{ fontSize: 12 }}>
            <Text small>
              Total upgrade cost:
            </Text>
            <UpgradeCost costs={_.values(upgradeCurrencyUsed)} />
          </Flex>
        )}
      />
    </Panel>
  );
};

TopGearPanel.propTypes = {
  topActor: PropTypes.object.isRequired, // eslint-disable-line
  focusActor: PropTypes.object.isRequired, // eslint-disable-line
  classId: PropTypes.number.isRequired,
  specName: PropTypes.string.isRequired,
  baseTalents: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  level: PropTypes.number.isRequired,
  preferEquipped: PropTypes.bool.isRequired,
  onClickPreferEquipped: PropTypes.func.isRequired,
};

TopGearPanel.defaultProps = {
};

export default TopGearPanel;
